import {
  Box,
  Button,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { PageTitle, Percentage } from "../../../components/Typography";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import { classes } from "../../../utils/class";
import { useNavigate } from "react-router";
import { CardPrimary } from "../../../components/Card";
import { SearchBar } from "../../../components/CustomInput";
import { DataTable } from "../../../components/Table";
import { EditDetail, StateSpan } from "../../../components/StateSpan";
import {
  ACTIVATE_ITEM,
  ARCHIVE_ITEM,
  DEACTIVATE_ITEM,
  STATE_ACTIVE,
  STATE_ARCHIVE,
  STATE_INACTIVE,
  UNARCHIVE_ITEM,
  VIEW_ROW,
} from "../../../utils/type";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPickup } from "../../../slices/pickupSlice";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { useEffect, useState } from "react";
import {
  activatePickup,
  archivePickup,
  deactivatePickup,
  fetchPickupPoints,
  removePickup,
  unarchivePickup,
} from "../../../actions/pickup";
import { getDateStringFromNum } from "../../../utils/helpers";
import {
  BACKOFFICE,
  DRIVER,
  OWNER,
  SALES,
  SALES_MANAGER,
  WAREHOUSE_MANAGER,
} from "../../../utils/constants";

const PickupListPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const rows = useSelector((state) => state.pickup.pickupList);
  const extraInfo = useSelector((state) => state.pickup.extraInfo);
  const orgData = useSelector((state) => state.organizations.profile);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [isLoaded, setIsLoaded] = useState(false);
  const role = useSelector((state) => state.user.role);

  const columns = [
    {
      field: "name",
      width: 300,
      headerName: "Name",
      renderCell: (props) => {
        return (
          <>
            <Stack>
              <Stack>
                <Stack
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {props.row.name}
                </Stack>
              </Stack>
              <Stack
                direction={"row"}
                gap={1}
                sx={{
                  fontSize: "12px",
                  fontWeight: "medium",
                  color: "#9F9E9E",
                }}
                className="mui-ellipsis"
              >
                <Stack>{props.row.official_id}</Stack>
                <Stack>{props.row.id}</Stack>
                <Stack>{props.row.producer}</Stack>
              </Stack>
            </Stack>
          </>
        );
      },
    },
    {
      field: "lastPicking",
      width: 150,
      headerName: "Last picking",
      renderCell: (props) => {
        const daysAgo = props.row.last_picking_days;
        if (daysAgo === null || daysAgo === undefined) return <>-</>;
        else
          return (
            <Stack direction={"row"} gap={1}>
              <Tooltip
                slotProps={{
                  tooltip: {
                    sx: {
                      background: "black",
                    },
                  },
                  arrow: {
                    sx: {
                      color: "black",
                    },
                  },
                }}
                title={getDateStringFromNum(
                  new Date(props.row.last_picking_date).getTime(),
                  orgData.timezone
                )}
                placement="top"
              >
                {props.row.last_picking_days} days ago
              </Tooltip>
              {props.row.last_picking_notes !== null && (
                <Tooltip
                  slotProps={{
                    tooltip: {
                      sx: {
                        background: "black",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "black",
                      },
                    },
                  }}
                  title={props.row.last_picking_notes}
                  placement="top"
                >
                  <CommentOutlinedIcon
                    sx={{
                      color: "text.light",
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          );
      },
    },
    {
      field: "qt",
      headerName: "Qt ",
      renderCell: (props) => {
        return (
          <Stack direction={"row"} gap={1}>
            {props.row.total_quantity}
            <Tooltip
              slotProps={{
                tooltip: {
                  sx: {
                    background: "black",
                  },
                },
                arrow: {
                  sx: {
                    color: "black",
                  },
                },
              }}
              title={`${props.row.ratio}% less than expected (${props.row.total_quantity_expected}kg)`}
              placement="top"
            >
              <Box
                alignItems={"center"}
                justifyContent={"center"}
                display={"flex"}
              >
                <Percentage value={props.row?.ratio} />
              </Box>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: "nextPicking",
      headerName: "Next collection",
      width: 150,
      renderCell: (props) => {
        if (!props.row.next_picking_date) return <>-</>;
        else
          return (
            <span className="mui-ellipsis">
              In {props.row.next_picking_date} days
            </span>
          );
      },
    },
    {
      field: "manager",
      headerName: "Manager",
      width: 150,
      renderCell: (props) => (
        <span className="mui-ellipsis">{props.value}</span>
      ),
    },
    {
      field: "zones",
      headerName: "Zone",
      width: "200",
      renderCell: (props) => (
        <span className="mui-ellipsis">{props.value}</span>
      ),
    },
    {
      field: "location_g",
      headerName: "Location",
      width: 200,
      renderCell: (props) => {
        if (props.row.addr_city) {
          if (
            props.row.latitude !== undefined &&
            props.row.longitude !== undefined
          )
            return (
              <>
                <Box
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                      textDecoration: "underline",
                    },
                  }}
                  className="mui-ellipsis"
                >
                  <Tooltip
                    slotProps={{
                      tooltip: {
                        sx: {
                          background: "black",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "black",
                        },
                      },
                    }}
                    placement="top"
                    title="Go to map"
                  >
                    {props.row.addr_city + "," + props.row.addr_street}
                  </Tooltip>
                </Box>
              </>
            );
          else {
            return (
              <>
                <Tooltip
                  slotProps={{
                    tooltip: {
                      sx: {
                        background: "black",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "black",
                      },
                    },
                  }}
                  placement="top"
                  title="No coordinates"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_8587_152593)">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8 1.33333C6.58551 1.33333 5.22896 1.89524 4.22876 2.89543C3.22857 3.89562 2.66667 5.25218 2.66667 6.66667C2.66667 8.08467 3.3001 9.4738 4.17403 10.7087L6.20071 8.63485C5.66766 8.14727 5.33333 7.44601 5.33333 6.66667C5.33333 5.19391 6.52724 4 8 4C8.7573 4 9.44087 4.31568 9.9263 4.82262L11.7906 2.91493C11.7842 2.90841 11.7777 2.90191 11.7712 2.89543C10.771 1.89524 9.41449 1.33333 8 1.33333ZM12.7226 1.96124C12.7198 1.95836 12.7169 1.95549 12.714 1.95262C11.4638 0.702379 9.76811 0 8 0C6.23189 0 4.5362 0.702379 3.28595 1.95262C2.03571 3.20286 1.33333 4.89856 1.33333 6.66667C1.33333 8.5311 2.18412 10.2558 3.22817 11.6765L0.141357 14.8351L1.33334 16L4.29428 12.9702C4.36836 13.0513 4.44235 13.1307 4.51603 13.2085C5.29293 14.0285 6.06756 14.6954 6.64722 15.1569C6.93757 15.3881 7.18036 15.5689 7.35186 15.6928C7.43764 15.7547 7.50569 15.8025 7.55304 15.8353C7.57672 15.8517 7.59523 15.8644 7.60821 15.8732L7.62349 15.8835L7.62793 15.8865L7.62984 15.8878C7.63003 15.8879 7.6302 15.888 8 15.3333L7.6302 15.888C7.85413 16.0373 8.14587 16.0373 8.3698 15.888L8 15.3333C8.3698 15.888 8.36997 15.8879 8.37016 15.8878L8.37207 15.8865L8.37651 15.8835L8.39179 15.8732C8.40477 15.8644 8.42328 15.8517 8.44696 15.8353C8.49431 15.8025 8.56236 15.7547 8.64814 15.6928C8.81964 15.5689 9.06243 15.3881 9.35279 15.1569C9.93245 14.6954 10.7071 14.0285 11.484 13.2085C13.0105 11.5972 14.6667 9.26796 14.6667 6.66667C14.6667 5.47083 14.3454 4.30813 13.7506 3.29396L15.6667 1.33333L14.4747 0.168447L12.7226 1.96124ZM12.7754 4.29182L10.6586 6.45786C10.6639 6.52676 10.6667 6.5964 10.6667 6.66667C10.6667 8.13943 9.47276 9.33333 8 9.33333C7.95048 9.33333 7.90127 9.33199 7.85241 9.32932L5.22781 12.015C5.31259 12.1086 5.39806 12.2008 5.48397 12.2915C6.20707 13.0548 6.93245 13.6796 7.47778 14.1139C7.68074 14.2755 7.85802 14.4101 8 14.5151C8.14198 14.4101 8.31926 14.2755 8.52222 14.1139C9.06756 13.6796 9.79293 13.0548 10.516 12.2915C11.9895 10.7362 13.3333 8.73204 13.3333 6.66667C13.3333 5.83478 13.139 5.02293 12.7754 4.29182ZM8.99336 5.77725C8.74923 5.50478 8.39464 5.33333 8 5.33333C7.26362 5.33333 6.66667 5.93029 6.66667 6.66667C6.66667 7.07232 6.84782 7.43567 7.13365 7.68021L8.99336 5.77725Z"
                        fill="#6D6D6D"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_8587_152593">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </Tooltip>
                {props.row.addr_city + "," + props.row.addr_street}
              </>
            );
          }
        }
      },
    },
    {
      field: "alert",
      headerName: "Alert",
      renderCell: (props) => {
        let alert_string = "";
        if (props.row.count_expired_documents > 0)
          alert_string = `${props.row.count_expiring_documents} docs expiring`;
        let raw_string = "";
        if (props.row.alert_list.length > 0) {
          for (let alert of props.row.alert_list) {
            raw_string += ` ${alert.raw_waste_name}`;
          }
          alert_string = alert_string + `${raw_string} - overflow`;
        }
        return (
          <>
            {alert_string.length > 0 && (
              <Tooltip
                slotProps={{
                  tooltip: {
                    sx: {
                      background: "black",
                    },
                  },
                  arrow: {
                    sx: {
                      color: "black",
                    },
                  },
                }}
                title={alert_string}
                placement="top"
              >
                <ReportProblemOutlinedIcon sx={{ color: "red" }} />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      field: "status",
      headerName: "status",
      width: 150,
      renderCell: (props) => {
        let options = [];

        if (role === OWNER || role === BACKOFFICE) {
          if (props.row.status === "active") {
            options = [{ label: "Inactive", value: "inactive" }];
          } else {
            if (props.row?.archived) {
              options = [{ label: "Unarchive", value: "unarchive" }];
            } else {
              options = [
                { label: "Active", value: "active" },
                { label: "Archive", value: "archive" },
              ];
            }
          }
        }

        if (role === WAREHOUSE_MANAGER || role === DRIVER) {
          options = [];
        }

        if (role === SALES_MANAGER || role === SALES) {
          if (props.row.status === "active") {
            options = [{ label: "Inactive", value: "inactive" }];
          } else {
            options = [{ label: "Active", value: "active" }];
          }
        }

        return (
          <StateSpan
            status={props.row.archived ? "archive" : props.value}
            options={options}
            onStateChangeHandler={(status) => {
              if (status === "active") {
                dispatch(activatePickup(props.row._id));
              } else if (status === "inactive") {
                dispatch(deactivatePickup(props.row._id));
              } else if (status === "archive") {
                dispatch(archivePickup(props.row._id));
              } else if (status === "unarchive") {
                dispatch(unarchivePickup(props.row._id));
              }
            }}
          />
        );
      },
    },
    {
      maxWidth: 30,
      field: "actions",
      type: "actions",
      hideSortIcons: true,
      disableColumnMenu: true,
      renderCell: (props) => {
        let options = [];

        if (role === OWNER || role === BACKOFFICE) {
          if (props.row.status === "active") {
            options = [
              {
                label: "View collection point",
                value: "action view",
              },
              {
                label: "Deactive collection point",
                value: "deactive",
              },
              {
                label: "Remove collection point",
                value: "action remove",
              },
            ];
          } else {
            if (props.row.archived) {
              options = [
                {
                  label: "View collection point",
                  value: "action view",
                },
                {
                  label: "Unarchive collection point",
                  value: "unarchive",
                },
                {
                  label: "Remove collection point",
                  value: "action remove",
                },
              ];
            } else {
              options = [
                {
                  label: "View collection point",
                  value: "action view",
                },
                {
                  label: "Active collection point",
                  value: "active",
                },
                {
                  label: "Archive collection point",
                  value: "archive",
                },
                {
                  label: "Remove collection point",
                  value: "action remove",
                },
              ];
            }
          }
        }
        if (role === WAREHOUSE_MANAGER || role === DRIVER) {
          options = [{ label: "View collection point", value: "action view" }];
        }
        if (role === SALES_MANAGER) {
          if (props.row.status === "active") {
            options = [
              {
                label: "View collection point",
                value: "action view",
              },
              {
                label: "Deactive collection point",
                value: "deactive",
              },
              {
                label: "Remove collection point",
                value: "action remove",
              },
            ];
          } else {
            if (props.row.archived) {
              options = [
                {
                  label: "View collection point",
                  value: "action view",
                },
                {
                  label: "Remove collection point",
                  value: "action remove",
                },
              ];
            } else {
              options = [
                {
                  label: "View collection point",
                  value: "action view",
                },
                {
                  label: "Active collection point",
                  value: "active",
                },
                {
                  label: "Remove collection point",
                  value: "action remove",
                },
              ];
            }
          }
        }
        if (role === SALES) {
          if (props.row.status === "active") {
            options = [
              {
                label: "View collection point",
                value: "action view",
              },
              {
                label: "Deactive collection point",
                value: "deactive",
              },
            ];
          } else {
            options = [
              {
                label: "View collection point",
                value: "action view",
              },
              {
                label: "Active collection point",
                value: "active",
              },
            ];
          }
        }

        return (
          <EditDetail
            options={options}
            onStateChangeHandler={(state) => {
              if (state === "action view") {
                dispatch(setCurrentPickup(props.row._id));
                navigate("/collection-detail");
              } else if (state === "deactive") {
                dispatch(deactivatePickup(props.row._id));
              } else if (state === "active") {
                dispatch(activatePickup(props.row._id));
              } else if (state === "archive") {
                dispatch(archivePickup(props.row._id));
              } else if (state === "unarchive") {
                dispatch(unarchivePickup(props.row._id));
              } else if (state === "action remove") {
                dispatch(removePickup(props.row._id));
              }
            }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    dispatch(
      fetchPickupPoints(() => {
        setIsLoaded(true);
      })
    );
  }, [dispatch]);

  if (rows.length === 0 && isLoaded)
    return (
      <>
        <Box sx={{ minHeight: "100vh", height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "20px",
              paddingX: "60px",
            }}
          >
            <PageTitle>Collection Points</PageTitle>
            <Button
              sx={classes.buttonPrimary}
              onClick={() => {
                navigate("/add-collection");
              }}
              disabled={role === WAREHOUSE_MANAGER}
            >
              Add collection point
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "80vh",
            }}
          >
            <HomeOutlinedIcon sx={{ color: "text.tertiary", fontSize: 100 }} />
            <Typography
              sx={{
                textAlign: "center",
                fontFamily: "Manrope",
                fontSize: "10px",
                color: "text.primary",
                marginBottom: "30px",
              }}
            >
              There are no records listed
            </Typography>
            <Button
              size="large"
              sx={classes.buttonPrimary}
              onClick={() => {
                navigate("/add-collection");
              }}
              disabled={role === WAREHOUSE_MANAGER}
            >
              Add collection point
            </Button>
          </Box>
        </Box>
      </>
    );
  else
    return (
      <Stack direction={"column"} marginTop={3} gap={2} paddingX="60px">
        <Stack direction={"row"} justifyContent={"space-between"}>
          <PageTitle>Collection Points List</PageTitle>
          <Stack justifyContent={"center"} alignItems={"center"}>
            <Button
              variant="outlined"
              sx={classes.buttonPrimary}
              onClick={() => {
                navigate("/add-collection");
              }}
              disabled={role === WAREHOUSE_MANAGER}
            >
              Add collection point
            </Button>
          </Stack>
        </Stack>
        <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
          {isLoaded ? (
            <CardPrimary
              type={1}
              title={"Without manager"}
              content={extraInfo?.count_without_manager}
            />
          ) : (
            <Skeleton variant="rounded" width={"162px"} height={"86px"} />
          )}
          {isLoaded ? (
            <CardPrimary
              type={2}
              title={"Expiring documents"}
              content={extraInfo?.count_expiring_documents}
            />
          ) : (
            <Skeleton variant="rounded" width={"162px"} height={"86px"} />
          )}
          {isLoaded ? (
            <CardPrimary
              type={3}
              title={"Expired date picking"}
              content={extraInfo?.count_expired_documents}
            />
          ) : (
            <Skeleton variant="rounded" width={"162px"} height={"86px"} />
          )}
          {isLoaded ? (
            <CardPrimary
              type={4}
              title={"With 0 waste collected"}
              content={extraInfo?.count_with_zero_waste_collected}
            />
          ) : (
            <Skeleton variant="rounded" width={"162px"} height={"86px"} />
          )}
        </Stack>
        <Stack justifyContent={"flex-start"}>
          {isLoaded ? (
            <SearchBar
              placeholder="Search collection point"
              sx={{ width: "275px" }}
              onSearchChange={(value) => {
                setFilterModel({
                  items: [
                    {
                      field: "name",
                      operator: "contains",
                      value: value,
                    },
                  ],
                });
              }}
            />
          ) : (
            <Skeleton variant="rounded" height={"30px"} width={"275px"} />
          )}
        </Stack>
        <Box>
          {isLoaded ? (
            <DataTable
              initialState={{
                pinnedColumns: { right: ["actions"] },
              }}
              fit="100%"
              filterModel={filterModel}
              rows={rows.map((row, index) => {
                return { ...row, index: index };
              })}
              columns={columns}
              onRowClick={(e) => {
                dispatch(setCurrentPickup(e.row._id));
                navigate("/collection-detail");
              }}
            />
          ) : (
            <Skeleton width={"100%"} height={"200px"} />
          )}
        </Box>
      </Stack>
    );
};
export default PickupListPage;
